import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback } from 'react';
import { Camera, X, RotateCcw, Zap, ZapOff } from 'lucide-react';
// @ts-ignore - jsQR não tem tipagem oficial
import jsQR from 'jsqr';
import Button from './ui/Button';
import Modal from './Modal';
const QRScanner = ({ isOpen, onClose, onScan, title = "Scanner QR Code" }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isScanning, setIsScanning] = useState(false);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [error, setError] = useState('');
    const [stream, setStream] = useState(null);
    const [scanResults, setScanResults] = useState([]);
    const [flashOn, setFlashOn] = useState(false);
    const intervalRef = useRef();
    const animationRef = useRef();
    const lastScanTime = useRef(0);
    const frameSkipCount = useRef(0);
    const processedHashes = useRef(new Set());
    // Inicializar câmera quando o modal abrir
    useEffect(() => {
        if (isOpen) {
            initializeCamera();
        }
        else {
            stopCamera();
        }
        return () => {
            stopCamera();
        };
    }, [isOpen]);
    // Buscar dispositivos disponíveis
    useEffect(() => {
        if (isOpen) {
            getDevices();
        }
    }, [isOpen]);
    const getDevices = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setDevices(videoDevices);
            // Selecionar câmera traseira por padrão
            const backCamera = videoDevices.find(device => device.label.toLowerCase().includes('back') ||
                device.label.toLowerCase().includes('traseira'));
            setSelectedDeviceId(backCamera?.deviceId || videoDevices[0]?.deviceId || '');
        }
        catch (err) {
            console.error('Erro ao obter dispositivos:', err);
            setError('Erro ao acessar dispositivos de câmera');
        }
    };
    const initializeCamera = async () => {
        try {
            setError('');
            // Parar stream anterior se existir
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            const constraints = {
                video: {
                    deviceId: selectedDeviceId || undefined,
                    facingMode: selectedDeviceId ? undefined : { ideal: 'environment' },
                    width: { ideal: 1280, min: 640 },
                    height: { ideal: 720, min: 480 },
                    frameRate: { ideal: 30, max: 30 }
                }
            };
            const newStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(newStream);
            if (videoRef.current) {
                videoRef.current.srcObject = newStream;
                videoRef.current.play();
                videoRef.current.onloadedmetadata = () => {
                    startScanning();
                };
            }
        }
        catch (err) {
            console.error('Erro ao inicializar câmera:', err);
            if (err.name === 'NotAllowedError') {
                setError('Permissão para câmera negada. Por favor, permita o acesso à câmera.');
            }
            else if (err.name === 'NotFoundError') {
                setError('Nenhuma câmera encontrada no dispositivo.');
            }
            else {
                setError('Erro ao acessar a câmera: ' + err.message);
            }
        }
    };
    const stopCamera = () => {
        setIsScanning(false);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };
    const startScanning = () => {
        if (!videoRef.current || !canvasRef.current)
            return;
        setIsScanning(true);
        lastScanTime.current = Date.now();
        frameSkipCount.current = 0;
        // Usar requestAnimationFrame para melhor performance
        const scanLoop = () => {
            if (isScanning) {
                scanQRCode();
                animationRef.current = requestAnimationFrame(scanLoop);
            }
        };
        scanLoop();
    };
    const scanQRCode = () => {
        if (!videoRef.current || !canvasRef.current || !isScanning)
            return;
        const now = Date.now();
        // Throttle: processar no máximo a cada 200ms para melhor performance
        if (now - lastScanTime.current < 200) {
            return;
        }
        // Skip frames para reduzir carga de processamento
        frameSkipCount.current++;
        if (frameSkipCount.current % 3 !== 0) {
            return;
        }
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        if (!context || video.readyState !== video.HAVE_ENOUGH_DATA)
            return;
        // Usar resolução menor para melhor performance
        const targetWidth = 640;
        const targetHeight = 480;
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        // Desenhar com suavização desligada para melhor performance
        context.imageSmoothingEnabled = false;
        context.drawImage(video, 0, 0, targetWidth, targetHeight);
        // Obter dados da imagem apenas da área central (ROI - Region of Interest)
        const roiSize = 300;
        const roiX = (targetWidth - roiSize) / 2;
        const roiY = (targetHeight - roiSize) / 2;
        const imageData = context.getImageData(roiX, roiY, roiSize, roiSize);
        // Usar jsQR com configurações otimizadas
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'dontInvert'
        });
        lastScanTime.current = now;
        if (code) {
            handleScanResult(code.data);
        }
    };
    // Função para gerar hash simples de string
    const simpleHash = useCallback((str) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Converter para 32bit integer
        }
        return hash.toString();
    }, []);
    const handleScanResult = useCallback((data) => {
        // Gerar hash do resultado para cache
        const dataHash = simpleHash(data);
        // Verificar se já foi processado recentemente
        if (processedHashes.current.has(dataHash)) {
            return;
        }
        // Adicionar ao cache e limpar entradas antigas
        processedHashes.current.add(dataHash);
        if (processedHashes.current.size > 10) {
            const entries = Array.from(processedHashes.current);
            processedHashes.current.clear();
            entries.slice(-5).forEach(hash => processedHashes.current.add(hash));
        }
        const result = {
            data,
            timestamp: new Date()
        };
        setScanResults(prev => [result, ...prev.slice(0, 4)]); // Manter últimos 5 resultados
        onScan(data);
        // Feedback visual/sonoro
        if (navigator.vibrate) {
            navigator.vibrate(200);
        }
        // Pausar escaneamento por 1 segundo após sucesso para evitar spam
        setIsScanning(false);
        setTimeout(() => {
            if (videoRef.current) {
                setIsScanning(true);
                startScanning();
            }
        }, 1000);
        // Limpar cache após 3 segundos
        setTimeout(() => {
            processedHashes.current.delete(dataHash);
        }, 3000);
    }, [simpleHash, onScan]);
    const toggleFlash = async () => {
        if (!stream)
            return;
        try {
            const track = stream.getVideoTracks()[0];
            const capabilities = track.getCapabilities();
            if (capabilities.torch) {
                await track.applyConstraints({
                    advanced: [{ torch: !flashOn }]
                });
                setFlashOn(!flashOn);
            }
        }
        catch (err) {
            console.error('Erro ao controlar flash:', err);
        }
    };
    const switchCamera = async () => {
        if (devices.length <= 1)
            return;
        const currentIndex = devices.findIndex(device => device.deviceId === selectedDeviceId);
        const nextIndex = (currentIndex + 1) % devices.length;
        const nextDevice = devices[nextIndex];
        setSelectedDeviceId(nextDevice.deviceId);
        await initializeCamera();
    };
    const formatScanResult = (data) => {
        try {
            const parsed = JSON.parse(data);
            return `${parsed.type?.toUpperCase()} #${parsed.id}`;
        }
        catch {
            return data.length > 30 ? `${data.substring(0, 30)}...` : data;
        }
    };
    return (_jsx(Modal, { isOpen: isOpen, onClose: onClose, title: title, size: "lg", children: _jsxs("div", { className: "space-y-4", children: [error ? (_jsx("div", { className: "text-center py-8", children: _jsxs("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4", children: [_jsxs("div", { className: "text-red-600 mb-4", children: [_jsx(Camera, { className: "h-12 w-12 mx-auto mb-2" }), _jsx("p", { className: "font-medium", children: error })] }), _jsx(Button, { onClick: initializeCamera, leftIcon: _jsx(RotateCcw, { className: "h-4 w-4" }), children: "Tentar Novamente" })] }) })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "relative bg-black rounded-lg overflow-hidden", children: [_jsx("video", { ref: videoRef, className: "w-full h-64 object-cover", autoPlay: true, playsInline: true, muted: true }), _jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: _jsxs("div", { className: "w-48 h-48 border-2 border-white rounded-lg relative", children: [_jsx("div", { className: "absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-500" }), _jsx("div", { className: "absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-500" }), _jsx("div", { className: "absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-500" }), _jsx("div", { className: "absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-500" }), isScanning && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: _jsx("div", { className: "w-full h-0.5 bg-red-500 animate-pulse" }) }))] }) }), _jsxs("div", { className: "absolute top-4 right-4 flex gap-2", children: [devices.length > 1 && (_jsx(Button, { variant: "ghost", size: "sm", onClick: switchCamera, className: "bg-black/50 text-white hover:bg-black/70", children: _jsx(RotateCcw, { className: "h-4 w-4" }) })), _jsx(Button, { variant: "ghost", size: "sm", onClick: toggleFlash, className: "bg-black/50 text-white hover:bg-black/70", children: flashOn ? _jsx(Zap, { className: "h-4 w-4" }) : _jsx(ZapOff, { className: "h-4 w-4" }) })] }), _jsx("div", { className: "absolute bottom-4 left-4 right-4", children: _jsx("div", { className: "bg-black/50 text-white text-center py-2 px-4 rounded", children: isScanning ? (_jsxs("div", { className: "flex items-center justify-center gap-2", children: [_jsx("div", { className: "w-2 h-2 bg-green-500 rounded-full animate-pulse" }), _jsx("span", { className: "text-sm", children: "Escaneando..." })] })) : (_jsx("span", { className: "text-sm", children: "Posicione o QR code na \u00E1rea destacada" })) }) })] }), _jsx("canvas", { ref: canvasRef, className: "hidden" }), scanResults.length > 0 && (_jsxs("div", { className: "bg-gray-50 rounded-lg p-4", children: [_jsx("h4", { className: "font-medium text-gray-900 mb-3", children: "Resultados Recentes" }), _jsx("div", { className: "space-y-2 max-h-32 overflow-y-auto", children: scanResults.map((result, index) => (_jsxs("div", { className: "flex items-center justify-between bg-white p-2 rounded border text-sm", children: [_jsx("span", { className: "font-mono", children: formatScanResult(result.data) }), _jsx("span", { className: "text-gray-500 text-xs", children: result.timestamp.toLocaleTimeString('pt-PT') })] }, index))) })] })), _jsx("div", { className: "bg-blue-50 border border-blue-200 rounded-lg p-4", children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx(Camera, { className: "h-5 w-5 text-blue-600 mt-0.5" }), _jsxs("div", { className: "text-sm text-blue-800", children: [_jsx("p", { className: "font-medium mb-1", children: "Como usar:" }), _jsxs("ul", { className: "space-y-1 text-xs", children: [_jsx("li", { children: "\u2022 Posicione o QR code dentro da \u00E1rea destacada" }), _jsx("li", { children: "\u2022 Mantenha o dispositivo est\u00E1vel" }), _jsx("li", { children: "\u2022 Certifique-se de que h\u00E1 luz suficiente" }), _jsx("li", { children: "\u2022 O escaneamento \u00E9 autom\u00E1tico" })] })] })] }) })] })), _jsxs("div", { className: "flex justify-end gap-3 pt-4 border-t border-gray-200", children: [_jsx(Button, { variant: "outline", onClick: onClose, children: "Fechar" }), !error && (_jsxs(Button, { onClick: isScanning ? () => setIsScanning(false) : startScanning, leftIcon: isScanning ? _jsx(X, { className: "h-4 w-4" }) : _jsx(Camera, { className: "h-4 w-4" }), variant: isScanning ? "outline" : "primary", children: [isScanning ? 'Parar' : 'Iniciar', " Escaneamento"] }))] })] }) }));
};
export default QRScanner;
